import { FormikErrors } from 'formik'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { companyServices } from 'services'
import { useParams } from 'react-router-dom'
import { isEmpty } from 'lodash'
import { Option } from 'types/conmon'
import { FormValues } from '../hook'

export type FieldWordMapWrapperProps = {
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined,
  ) => Promise<void> | Promise<FormikErrors<FormValues>>
  setValues: (
    values: React.SetStateAction<FormValues>,
    shouldValidate?: boolean | undefined,
  ) => Promise<void> | Promise<FormikErrors<FormValues>>
  values: FormValues
  handleResetDataChart: () => void
  setValueSwitch: React.Dispatch<React.SetStateAction<boolean>>
  setValueExplanation: React.Dispatch<
    React.SetStateAction<{
      loading: boolean
      data: string
      done: boolean
    }>
  >
  handleOffSwitch: () => void
}

type ModeSelectionValues = Pick<
  FormValues,
  | 'term'
  | 'sort_by'
  | 'quarters'
  | 'targetYear'
  | 'search_key'
  | 'display_size'
  | 'multi_company'
  | 'targetDocument'
  | 'number_of_word'
>

export const useFieldWordMapWrapper = (props: FieldWordMapWrapperProps) => {
  const { id } = useParams()
  const {
    values,
    setValues,
    handleResetDataChart,
    setValueSwitch,
    setValueExplanation,
  } = props
  const [yearQuarters, setYearQuaters] = useState<{ [key: string]: number[] }>(
    {},
  )
  const modeSelectionsRef = useRef<Record<string, ModeSelectionValues>>({
    '0': {
      term: values.term,
      sort_by: values.sort_by,
      quarters: values.quarters,
      targetYear: values.targetYear,
      search_key: values.search_key,
      display_size: values.display_size,
      multi_company: values.multi_company,
      targetDocument: values.targetDocument,
      number_of_word: values.number_of_word,
    },
    '1': {
      term: values.term,
      sort_by: values.sort_by,
      quarters: values.quarters,
      targetYear: values.targetYear,
      search_key: values.search_key,
      display_size: values.display_size,
      multi_company: values.multi_company,
      targetDocument: values.targetDocument,
      number_of_word: values.number_of_word,
    },
    '2': {
      term: values.term,
      sort_by: values.sort_by,
      quarters: values.quarters,
      targetYear: values.targetYear,
      search_key: values.search_key,
      display_size: values.display_size,
      multi_company: values.multi_company,
      targetDocument: values.targetDocument,
      number_of_word: values.number_of_word,
    },
  })

  const reportYearsOptions = useMemo(() => {
    if (isEmpty(yearQuarters)) return []
    return Object.keys(yearQuarters)
      .reverse()
      .map(o => ({
        label: `${o}年度`,
        value: `${o}`,
      }))
  }, [yearQuarters])

  const reportQuartersOptions = useMemo(() => {
    if (!(values.targetYear && yearQuarters)) return []
    return (yearQuarters[values.targetYear] || []).map(o => ({
      label: `第${o}四半期`,
      value: `${o}`,
    }))
  }, [values.targetYear, yearQuarters])

  const termOptions: Option[] = useMemo(() => {
    if (Object.keys(yearQuarters).length === 0) return []

    if (!['2', '4', '5'].includes(values.targetDocument)) {
      if (!values.targetYear || !values.quarters) return []
    } else {
      if (!values.targetYear) return []
    }

    let count = 0

    if (!['2', '4', '5'].includes(values.targetDocument)) {
      for (const year in yearQuarters) {
        if (
          Number(year) <= Number(values.targetYear) &&
          yearQuarters[year].includes(+values.quarters)
        ) {
          count += 1
        }
        if (count === 5) break
      }
    } else {
      for (const year in yearQuarters) {
        if (Number(year) <= Number(values.targetYear)) {
          count += 1
        }
        if (count === 5) break
      }
    }

    return Array.from(Array(count).keys()).map(o => ({
      label: `${o + 1}期分`,
      value: `${o + 1}`,
    }))
  }, [values.quarters, values.targetDocument, values.targetYear, yearQuarters])

  const handleResetForm = (
    e:
      | {
          label: string
          value: string | number
        }
      | undefined,
  ) => {
    if (!e || e.value === values.analysis_menu) return
    const nextMode = String(e?.value)

    setValues(prev => {
      modeSelectionsRef.current[prev.analysis_menu] = {
        term: prev.term,
        sort_by: prev.sort_by,
        quarters: prev.quarters,
        targetYear: prev.targetYear,
        search_key: prev.search_key,
        display_size: prev.display_size,
        multi_company: prev.multi_company,
        targetDocument: prev.targetDocument,
        number_of_word: prev.number_of_word,
      }

      return {
        ...prev,
        ...modeSelectionsRef.current[nextMode],
        analysis_menu: nextMode,
      }
    })
    handleResetDataChart()
    setValueSwitch(false)
    setValueExplanation(prev => ({
      ...prev,
      loading: false,
      data: '',
      done: true,
    }))
  }

  const fetchReportYearQuarters = useCallback(async () => {
    try {
      if (!id || !values.targetDocument) return

      const { data } = await companyServices.getReportYearQuarters({
        company_id: +id,
        type_of_process: +values.targetDocument,
      })

      if (isEmpty(data)) return

      setYearQuaters({ ...data })
    } catch (error) {
      console.log('error', error)
    }
  }, [id, values.targetDocument])

  useEffect(() => {
    fetchReportYearQuarters()
  }, [fetchReportYearQuarters])

  useEffect(() => {
    modeSelectionsRef.current[values.analysis_menu] = {
      term: values.term,
      sort_by: values.sort_by,
      quarters: values.quarters,
      targetYear: values.targetYear,
      search_key: values.search_key,
      display_size: values.display_size,
      multi_company: values.multi_company,
      targetDocument: values.targetDocument,
      number_of_word: values.number_of_word,
    }
  }, [
    values.analysis_menu,
    values.display_size,
    values.multi_company,
    values.number_of_word,
    values.quarters,
    values.search_key,
    values.sort_by,
    values.targetDocument,
    values.targetYear,
    values.term,
  ])

  useEffect(() => {
    if (isEmpty(yearQuarters)) return

    const sortedYears = Object.keys(yearQuarters).sort(
      (a, b) => Number(b) - Number(a),
    )
    const latestYear = sortedYears[0]
    if (!latestYear) return

    const isQuarterRequired = !['2', '4', '5'].includes(values.targetDocument)
    const latestQuarter = Math.max(...(yearQuarters[latestYear] || []))

    const shouldSetYear = !values.targetYear
    const shouldSetQuarter =
      isQuarterRequired && !values.quarters && Number.isFinite(latestQuarter)
    const shouldSetTerm = !values.term

    if (!shouldSetYear && !shouldSetQuarter && !shouldSetTerm) return

    setValues(prev => ({
      ...prev,
      targetYear: shouldSetYear ? latestYear : prev.targetYear,
      quarters: shouldSetQuarter ? String(latestQuarter) : prev.quarters,
      term: shouldSetTerm ? '1' : prev.term,
    }))
  }, [
    yearQuarters,
    setValues,
    values.targetDocument,
    values.targetYear,
    values.quarters,
    values.term,
  ])

  return {
    ...props,
    handleResetForm,
    setYearQuaters,
    yearQuarters,
    reportYearsOptions,
    reportQuartersOptions,
    termOptions,
  }
}
